import React, { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import PropTypes, { arrayOf } from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import DeleteButton from '../buttons/DeleteButton';
import TextField from '../fields/TextField';
import TextAreaField from '../fields/TextAreaField';
import RecipientsField from '../fields/RecipientsField';
import SelectField from '../fields/SelectField';
import CheckboxList from '../fields/CheckboxList';
import EmailTemplatePlaceholders from './EmailTemplatePlaceholders';
import './_styles_/EmailTemplateTab.css';
import {
    EMAIL_TEMPLATE_BODY_LENGTH,
    EMAIL_TEMPLATE_SUBJECT_LENGTH,
    EMAIL_TEMPLATE_NAME_LENGTH,
    EMAIL_TEMPLATE_RECIPIENTS_LENGTH,
    emailTemplateBodyValidator,
    emailTemplateNameExistsValidator,
    emailTemplateNameValidator,
    emailTemplateRecipientsValidator,
    emailTemplateSubjectValidator,
} from '../../validators/emailTemplates';
import useExtendedState from '../../hooks/useExtendedState';
import useMultiValidator from '../../hooks/useMultiValidator';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_EMAIL_TEMPLATES_QUERY_NAME, DELETE_EMAIL_TEMPLATE, UPDATE_EMAIL_TEMPLATE } from '../../graphql/emailTemplates';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';
import { Privileged } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import { emailTemplateType, productType } from '../../types';
import {useDispatch} from "react-redux";

EmailTemplateDetailTab.propTypes = {
    availableProducts: arrayOf(productType),
    emailTemplate: emailTemplateType.isRequired,
    onDetailDelete: PropTypes.func.isRequired,
};

function EmailTemplateDetailTab(props) {
    const {
        availableProducts = [],
        emailTemplate,
        onDetailDelete,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();

    const productState = useExtendedState(availableProducts.find((product) => product.productId === emailTemplate.productId));
    const phasesState = useExtendedState(emailTemplate.phases || []);
    const localeState = useExtendedState(emailTemplate.locale);
    const nameState = useExtendedState(emailTemplate.name);
    const recipientsState = useExtendedState(emailTemplate.recipients || '');
    const recipientsCcState = useExtendedState(emailTemplate.recipientsCc || '');
    const recipientsBccState = useExtendedState(emailTemplate.recipientsBcc || '');
    const subjectState = useExtendedState(emailTemplate.subject);
    const bodyState = useExtendedState(emailTemplate.body);

    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(
            emailTemplateNameValidator(t),
            emailTemplateNameExistsValidator(t, dispatch, apolloClient, nameState.initialValue)
        ),
        recipients: compositeValidator(emailTemplateRecipientsValidator(t), wafValidator(t)),
        recipientsCc: compositeValidator(emailTemplateRecipientsValidator(t, true), wafValidator(t)),
        recipientsBcc: compositeValidator(emailTemplateRecipientsValidator(t, true), wafValidator(t)),
        subject: compositeValidator(emailTemplateSubjectValidator(t), wafValidator(t)),
        body: compositeValidator(emailTemplateBodyValidator(t), (input) => wafValidator(t)(JSON.stringify(input).slice(1, -1))), // escape line feeds from input and trim apostrophes added by JSON.stringify
    });

    const keyToProduct = useCallback((key) => availableProducts.find((product) => product.productId === key) || null, [availableProducts]);
    const variables = {
        id: emailTemplate.id,
        productId: productState.value ? productState.value.id : null,
        phases: phasesState.value,
        locale: localeState.value,
        name: nameState.value,
        recipients: recipientsState.value,
        recipientsCc: recipientsCcState.value,
        recipientsBcc: recipientsBccState.value,
        subject: subjectState.value || null,
        body: bodyState.value,
    };

    const isChanged = nameState.isChanged || recipientsState.isChanged || recipientsCcState.isChanged
        || recipientsBccState.isChanged || subjectState.isChanged
        || bodyState.isChanged || productState.isChanged || phasesState.isChanged || localeState.isChanged;
    const isChangedOrModifiablePromise = !isChanged ? Promise.reject() : Promise.resolve(); // NOSONAR javascript:S6671
    const [isModificationDisabled, setIsModificationDisabled] = useState(true);
    Promise.all([isChangedOrModifiablePromise, isValid()])
        .then(() => setIsModificationDisabled(false))
        .catch(() => setIsModificationDisabled(true));

    const updateEmailTemplateMutation = useExtendedMutation(UPDATE_EMAIL_TEMPLATE, {
        variables,
        statesToResetOnSuccess: [productState, phasesState, localeState, nameState, recipientsState, recipientsCcState, recipientsBccState, subjectState, bodyState],
        successMessageOptions: { key: 'email-templates.notification.email-template-update-success', dataProperty: 'updateEmailTemplate' },
        failureMessageOptions: { key: 'email-templates.notification.email-template-update-failure', params: { name: nameState.value } },
        refetchQueries: [ALL_EMAIL_TEMPLATES_QUERY_NAME],
    });

    const deleteEmailTemplateMutation = useExtendedMutation(DELETE_EMAIL_TEMPLATE, {
        variables: { id: emailTemplate.id },
        successCallback: onDetailDelete,
        successMessageOptions: { key: 'email-templates.notification.email-template-delete-success', dataProperty: 'deleteEmailTemplate' },
        failureMessageOptions: { key: 'email-templates.notification.email-template-delete-failure', params: { name: emailTemplate.name, id: emailTemplate.id } },
        refetchQueries: [ALL_EMAIL_TEMPLATES_QUERY_NAME],
    });

    return (
        <ExpandedListTab
            className="email-template-detail-tab"
            title={t('email-templates.email-template-detail')}
        >
            <div className="rich-list-row-expanded--fields email-template-tab--fields">
                <div className="email-template-tab--left-col">
                    <TextField
                        label={t('email-templates.name')}
                        state={nameState}
                        className="email-template-detail-tab--fields--name wide"
                        wrapperClassName="email-template-tab--stacked"
                        inputId="email-template-detail-tab--fields--name"
                        validator={validators.name}
                        maxLength={EMAIL_TEMPLATE_NAME_LENGTH}
                        maximizeInputWidth={true}
                        isRequired
                    />
                    <SelectField
                        label={t('auctions.auction-setup.product')}
                        className="email-template-detail-tab--fields--product"
                        inputId="email-template-detail-tab--fields--product"
                        value={productState.value ? productState.value.productId : ''}
                        state={productState}
                        valueTransformer={keyToProduct}
                        options={availableProducts.map((product) => <option value={product.productId} key={product.productId}>{product.name} ({product.productId})</option>)}
                        isRequired
                    />
                    <CheckboxList
                        label={t('email-templates.phases')}
                        name="email-template-phases"
                        className="email-template-detail-tab--fields--phases"
                        values={[
                            { id: 'OPEN', label: t('email-templates.phase-running') },
                            { id: 'REJECTED', label: t('email-templates.phase-rejected') },
                            { id: 'FINISHED', label: t('email-templates.phase-finished') },
                        ]}
                        state={phasesState}
                    />
                    <SelectField
                        label={t('email-templates.language')}
                        className="email-template-detail-tab--fields--locale"
                        inputId="email-template-detail-tab--fields--locale"
                        state={localeState}
                        isRequired
                        options={[
                            <option value="en" key="en">{t('email-templates.language-english')}</option>,
                            <option value="de" key="de">{t('email-templates.language-german')}</option>,
                        ]}
                    />
                    <TextField
                        label={t('email-templates.subject')}
                        state={subjectState}
                        className="email-template-detail-tab--fields--subject wide"
                        wrapperClassName="email-template-tab--stacked"
                        inputId="email-template-detail-tab--fields--subject"
                        validator={validators.subject}
                        maxLength={EMAIL_TEMPLATE_SUBJECT_LENGTH}
                        maximizeInputWidth={true}
                        isRequired
                    />
                    <TextAreaField
                        label={t('email-templates.body')}
                        state={bodyState}
                        wrapperClassName="email-template-tab--body"
                        inputId="email-template-detail-tab--fields--body"
                        validator={validators.body}
                        maxLength={EMAIL_TEMPLATE_BODY_LENGTH}
                        placeholder={t('email-templates.body-placeholder')}
                        textInstructions={<EmailTemplatePlaceholders />}
                        isRequired
                    />
                </div>
                <div className="email-template-tab--recipients-group">
                    <RecipientsField
                        label={t('email-templates.recipients')}
                        state={recipientsState}
                        wrapperClassName="email-template-tab--recipients"
                        inputId="email-template-detail-tab--fields--recipients"
                        validator={validators.recipients}
                        maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                        placeholder={t('email-templates.recipients-placeholder')}
                        addRecipientLabel={t('email-templates.add-recipient')}
                        removeRecipientLabel={t('email-templates.remove-recipient')}
                        isRequired
                    />
                    <RecipientsField
                        label={`${t('email-templates.recipients')} - Cc`}
                        state={recipientsCcState}
                        wrapperClassName="email-template-tab--recipients-cc"
                        inputId="email-template-detail-tab--fields--recipients-cc"
                        validator={validators.recipientsCc}
                        maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                        placeholder={t('email-templates.recipients-placeholder')}
                        addRecipientLabel={t('email-templates.add-recipient')}
                        removeRecipientLabel={t('email-templates.remove-recipient')}
                    />
                </div>
                <RecipientsField
                    label={`${t('email-templates.recipients')} - Bcc`}
                    state={recipientsBccState}
                    wrapperClassName="email-template-tab--recipients-bcc"
                    inputId="email-template-detail-tab--fields--recipients-bcc"
                    validator={validators.recipientsBcc}
                    maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                    placeholder={t('email-templates.recipients-placeholder')}
                    addRecipientLabel={t('email-templates.add-recipient')}
                    removeRecipientLabel={t('email-templates.remove-recipient')}
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.EMAIL_TEMPLATE_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateEmailTemplateMutation}
                            disabled={isModificationDisabled}
                            confirmationMessage={t('email-templates.confirm-update-email-template', { name: nameState.value })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle" />
                    <div className="rich-list-row-expanded--buttons--right">
                        <DeleteButton
                            extendedMutation={deleteEmailTemplateMutation}
                            confirmationMessage={t('email-templates.confirm-delete-email-template', { name: emailTemplate.name })}
                        />
                    </div>
                </div>
            </Privileged>
        </ExpandedListTab>
    );
}

export default EmailTemplateDetailTab;
